import React, { useState, useEffect } from 'react';
import api from '../config/api';
import './ManagementPages.css';

const formatStatus = (status) => {
  if (!status) return '';
  return status.split('-').map(word => word.charAt(0).toUpperCase() + word.slice(1)).join(' ');
};

const Projects = () => {
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [editingProject, setEditingProject] = useState(null);

  const [formData, setFormData] = useState({
    name: '',
    description: '',
    client: {
      name: '',
      email: '',
      phone: '',
      address: '',
      taxId: ''
    },
    startDate: '',
    endDate: '',
    status: 'planning',
    teamMembers: []
  });

  useEffect(() => {
    fetchProjects();
  }, []);

  const fetchProjects = async () => {
    try {
      const response = await api.get('/api/projects');
      setProjects(response.data);
    } catch (err) {
      setError('Failed to load projects');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingProject) {
        await api.put(`/api/projects/${editingProject._id}`, formData);
      } else {
        await api.post('/api/projects', formData);
      }
      fetchProjects();
      closeModal();
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to save project');
    }
  };

  const handleDelete = async (id) => {
    if (window.confirm('Are you sure you want to delete this project?')) {
      try {
        await api.delete(`/api/projects/${id}`);
        fetchProjects();
      } catch (err) {
        setError('Failed to delete project');
      }
    }
  };

  const openModal = (project = null) => {
    if (project) {
      setEditingProject(project);
      setFormData({
        ...project,
        startDate: project.startDate?.split('T')[0],
        endDate: project.endDate?.split('T')[0] || ''
      });
    } else {
      setEditingProject(null);
      setFormData({
        name: '',
        description: '',
        client: {
          name: '',
          email: '',
          phone: '',
          address: '',
          taxId: ''
        },
        startDate: '',
        endDate: '',
        status: 'planning',
        teamMembers: []
      });
    }
    setShowModal(true);
  };

  const closeModal = () => {
    setShowModal(false);
    setEditingProject(null);
    setError('');
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    if (name.startsWith('client.')) {
      const clientField = name.split('.')[1];
      setFormData(prev => ({
        ...prev,
        client: { ...prev.client, [clientField]: value }
      }));
    } else {
      setFormData(prev => ({ ...prev, [name]: value }));
    }
  };

  if (loading) return <div className="loading">Loading...</div>;

  return (
    <div className="management-page">
      <div className="page-header">
        <h1>Project Management</h1>
        <button className="btn btn-primary" onClick={() => openModal()}>
          + Add Project
        </button>
      </div>

      {error && <div className="error">{error}</div>}

      <div className="table-container">
        <table>
          <thead>
            <tr>
              <th>Name</th>
              <th>Client</th>
              <th>Start Date</th>
              <th>Status</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {projects.map(project => (
              <tr key={project._id}>
                <td>{project.name}</td>
                <td>{project.client?.name}</td>
                <td>{new Date(project.startDate).toLocaleDateString()}</td>
                <td>
                  <span className={`status-badge status-${project.status}`}>
                    {formatStatus(project.status)}
                  </span>
                </td>
                <td>
                  <button
                    className="btn-icon btn-edit"
                    onClick={() => openModal(project)}
                    title="Edit"
                  >
                    ✏️
                  </button>
                  <button
                    className="btn-icon btn-delete"
                    onClick={() => handleDelete(project._id)}
                    title="Delete"
                  >
                    🗑️
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {showModal && (
        <div className="modal-overlay" onClick={closeModal}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>{editingProject ? 'Edit Project' : 'Add Project'}</h2>
              <button className="close-btn" onClick={closeModal}>×</button>
            </div>

            <form onSubmit={handleSubmit}>
              <div className="form-group">
                <label>Project Name *</label>
                <input name="name" value={formData.name} onChange={handleChange} required />
              </div>

              <div className="form-group">
                <label>Description</label>
                <textarea name="description" value={formData.description} onChange={handleChange} rows="3" />
              </div>

              <h3>Client Details</h3>
              <div className="form-group">
                <label>Client Name *</label>
                <input name="client.name" value={formData.client.name} onChange={handleChange} required />
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label>Client Email</label>
                  <input type="email" name="client.email" value={formData.client.email} onChange={handleChange} />
                </div>
                <div className="form-group">
                  <label>Client Phone</label>
                  <input name="client.phone" value={formData.client.phone} onChange={handleChange} />
                </div>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label>Client Address</label>
                  <input name="client.address" value={formData.client.address} onChange={handleChange} />
                </div>
                <div className="form-group">
                  <label>Tax ID</label>
                  <input name="client.taxId" value={formData.client.taxId} onChange={handleChange} />
                </div>
              </div>

              <h3>Project Details</h3>
              <div className="form-row">
                <div className="form-group">
                  <label>Start Date *</label>
                  <input type="date" name="startDate" value={formData.startDate} onChange={handleChange} required />
                </div>
                <div className="form-group">
                  <label>End Date</label>
                  <input type="date" name="endDate" value={formData.endDate} onChange={handleChange} />
                </div>
              </div>

              <div className="form-group">
                <label>Status *</label>
                <select name="status" value={formData.status} onChange={handleChange} required>
                  <option value="planning">Planning</option>
                  <option value="in-progress">In Progress</option>
                  <option value="on-hold">On Hold</option>
                  <option value="completed">Completed</option>
                  <option value="cancelled">Cancelled</option>
                </select>
              </div>

              <div className="form-actions">
                <button type="button" className="btn btn-secondary" onClick={closeModal}>Cancel</button>
                <button type="submit" className="btn btn-primary">
                  {editingProject ? 'Update' : 'Create'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default Projects;
